import React from "react";
import { observer } from "mobx-react";
import { Search, People } from "@mui/icons-material";
import { CircularProgress } from "@mui/material";
import { Button } from "@ann/web-components";
import { useContactsSelection } from "../../hooks";
import {
  Container,
  SearchBar,
  SearchInput,
  SelectionInfo,
  SelectionCount,
  SelectionActions,
  TableContainer,
  Table,
  TableHeader,
  TableBody,
  Checkbox,
  ContactName,
  ContactEmail,
  EmptyState,
  LoadingState,
  Pagination,
  PaginationInfo,
  PaginationButtons,
  ActionButtons,
} from "./ContactsFromDatabase.styled";

const ContactsFromDatabase = observer(({ onConfirm }) => {
  const {
    contacts,
    selectedCount,
    searchTerm,
    isLoading,
    pagination,
    allVisibleSelected,
    someVisibleSelected,
    handleSearchChange,
    handlePageChange,
    handleToggleContact,
    handleSelectAll,
    handleDeselectAll,
    confirmSelection,
  } = useContactsSelection();

  const handleConfirm = () => {
    const result = confirmSelection();
    if (result.success) {
      onConfirm?.();
    }
  };

  return (
    <Container>
      <SearchBar>
        <SearchInput>
          <Search />
          <input
            type="text"
            placeholder="Search contacts..."
            value={searchTerm}
            onChange={(e) => handleSearchChange(e.target.value)}
            aria-label="Search contacts"
          />
        </SearchInput>
      </SearchBar>

      {selectedCount > 0 && (
        <SelectionInfo>
          <SelectionCount>
            {selectedCount} contact{selectedCount !== 1 ? "s" : ""} selected
          </SelectionCount>
          <SelectionActions>
            <button type="button" onClick={handleDeselectAll}>
              Clear selection
            </button>
          </SelectionActions>
        </SelectionInfo>
      )}

      <TableContainer>
        {isLoading ? (
          <LoadingState>
            <CircularProgress />
          </LoadingState>
        ) : contacts.length === 0 ? (
          <EmptyState>
            <People />
            <p>No contacts found</p>
          </EmptyState>
        ) : (
          <>
            <Table>
              <TableHeader>
                <tr>
                  <th style={{ width: 50 }}>
                    <Checkbox
                      type="checkbox"
                      checked={allVisibleSelected}
                      ref={(el) => {
                        if (el) el.indeterminate = someVisibleSelected;
                      }}
                      onChange={() =>
                        allVisibleSelected ? handleDeselectAll() : handleSelectAll()
                      }
                      aria-label="Select all visible contacts"
                    />
                  </th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Location</th>
                </tr>
              </TableHeader>
              <TableBody>
                {contacts.map((contact) => (
                  <tr
                    key={contact.partyUuid}
                    onClick={() => handleToggleContact(contact.partyUuid)}
                  >
                    <td onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        type="checkbox"
                        checked={contact.isSelected}
                        onChange={() => handleToggleContact(contact.partyUuid)}
                        aria-label={`Select ${contact.legalName}`}
                      />
                    </td>
                    <td>
                      <ContactName>{contact.legalName || "—"}</ContactName>
                    </td>
                    <td>
                      <ContactEmail>{contact.email || "—"}</ContactEmail>
                    </td>
                    <td>{contact.phone || "—"}</td>
                    <td>
                      {contact.city && contact.state
                        ? `${contact.city}, ${contact.state}`
                        : contact.city || contact.state || "—"}
                    </td>
                  </tr>
                ))}
              </TableBody>
            </Table>

            {pagination && (
              <Pagination>
                <PaginationInfo>
                  Page {pagination.currentPage} of {pagination.totalPages || 1}
                  {pagination.totalItems > 0 && ` (${pagination.totalItems} total)`}
                </PaginationInfo>
                <PaginationButtons>
                  <button
                    type="button"
                    disabled={!pagination.hasPrevious}
                    onClick={() => handlePageChange(pagination.currentPage - 1)}
                  >
                    Previous
                  </button>
                  <button
                    type="button"
                    disabled={!pagination.hasNext}
                    onClick={() => handlePageChange(pagination.currentPage + 1)}
                  >
                    Next
                  </button>
                </PaginationButtons>
              </Pagination>
            )}
          </>
        )}
      </TableContainer>

      <ActionButtons>
        <Button
          type="primary"
          size="medium"
          onClick={handleConfirm}
          disabled={selectedCount === 0}
          label={`Confirm Selection (${selectedCount})`}
        />
      </ActionButtons>
    </Container>
  );
});

export default ContactsFromDatabase;
